import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, effect, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AlternativeTranslate, Categories, Items, ItemsVariations } from 'tilby-models';
import { VariationsFormService } from './variations-form.service';
import { AlertDialogService } from 'src/app/dialogs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatAccordion, MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { ListSelectorDialogService } from 'src/app/dialogs/list-selector-dialog/list-selector-dialog.component';
import { ImagesManagerService } from 'src/app/core/services/images-manager';
import { DragDropModule, transferArrayItem } from '@angular/cdk/drag-drop';
import { TilbyCurrencyPipe } from '@tilby/tilby-ui-lib/pipes/tilby-currency';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { debounceTime, distinctUntilChanged, Subject, Subscription, takeUntil} from 'rxjs';
import { Validators } from "@angular/forms";
import { TrimInputDirective } from 'src/app/directives/trim.directive';
import { EncodedData, price_difference_type } from 'src/app/features/items/utils/item.constants';
import { TruncatedTextDirective } from 'src/app/directives/truncated-text.directive';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ItemDataCatalogService } from 'src/app/features/items/services/item-data-catalog.service';
import { mobileCheck } from 'src/utilities';
import { ConfigurationManagerService } from 'src/app/core';

import { 
  ItemDataOperationsService,
  TranslationsInputData
} from 'src/app/features/items';

@Component({
  selector: 'app-variations',
  standalone: true,
  imports: [
    MatFormFieldModule,
    ReactiveFormsModule,
    TranslateModule,
    FormsModule,
    CommonModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatIconModule,
    MatButtonModule,
    MatExpansionModule,
    DragDropModule,
    MatChipsModule,
    MatAutocompleteModule,
    TrimInputDirective,
    TruncatedTextDirective,
    MatTooltipModule
  ],
  templateUrl: './variations.component.html',
  styleUrl: './variations.component.scss'
})
export class VariationsComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  readonly variationsFormService = inject(VariationsFormService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly translate = inject(TranslateService);
  private readonly listSelectorDialogService = inject(ListSelectorDialogService);
  private readonly configurationManagerService = inject(ConfigurationManagerService);
  private readonly itemDataCatalogService = inject(ItemDataCatalogService);
  private readonly itemDataOperationsService = inject(ItemDataOperationsService);

  public readonly Validators = Validators; 

  @Input() itemId: number = 0;
  @Input() categoryId: number = 0;
  @Input() combinationsActive: false = false;
  @Input() splitGroupComponents: false = false;
  @Input() variationsData: ItemsVariations[] = [];

  @Output() requestRemoveVariation = new EventEmitter<number>();
  @Output() requestRemoveVariationValue = new EventEmitter<{variationIndex: number, variationValueIndex: number}>();
  @Output() closeAllCombinationsPanels = new EventEmitter<boolean>();

  @ViewChild('variantAccordion') accordion: MatAccordion | undefined;
  @ViewChildren('variantValueAccordion') innerAccordion!: QueryList<MatAccordion>; 
  @ViewChildren(MatExpansionPanel) panels!: QueryList<MatExpansionPanel>;

  // ViewChildren per gestire lo scroll dei pannelli delle varianti (sia interni che esterni)
  @ViewChildren(MatExpansionPanel, { read: ElementRef }) elementRefPanels!:  QueryList<ElementRef>;

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChildren(MatAutocompleteTrigger) allAutocompleteTriggers!: QueryList<MatAutocompleteTrigger>;

  @ViewChildren('autocompleteInputElement', { read: ElementRef }) autocompleteInputs!: QueryList<ElementRef>;
  @ViewChildren(MatInput) matInputs!: QueryList<MatInput>;

  priceDifferenceTypeList: Array<EncodedData> = [];
  variationLinkableItems: Array<Items> = [];
  itemsWithVariation: Array<Items> = [];
  exits: Array<number> = [];
  
  private destroy$ = new Subject<void>(); 
  private variationNameSubscriptions = new Map<any, Subscription>();
  private variationValueNameSubscriptions = new Map<any, Subscription>();
  
  private focusSubscription!: Subscription;
  isDataListReady = this.itemDataCatalogService.isReady;
  variationsFormReady = this.variationsFormService.variationsFormReady;

  protected isMobile = mobileCheck();
  protected isAlternativeTranslateEnabled = !!this.configurationManagerService.getPreference('items.enable_alternative_translate');
  
  constructor() {
    this.initLists();

    effect(() => {
      if(this.isDataListReady()) {
        this.variationsFormService.createForm(this.variationsData);
        this.variationLinkableItems = this.variationsFormService.variationLinkableItems;
        this.itemsWithVariation = this.variationsFormService.itemsWithVariation;
        this.listenToVariationsChanges();
      }
    }, { allowSignalWrites: true })
  }

  initLists() {
    this.priceDifferenceTypeList = [...price_difference_type];

    const exitsCount = parseInt(this.configurationManagerService.getPreference('orders.exits') || '', 10) || 5;
    this.exits = Array.from({ length: exitsCount }, (_, i) => i + 1);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['splitGroupComponents'] && !changes['splitGroupComponents'].firstChange) {
      this.updateAllLinkedItemValidators();
    }
  }

  private updateAllLinkedItemValidators() {
    this.variationsFormService.variationsArray.controls.forEach((_, index) => {
      this.variationsFormService.getVariationValues(index)?.controls.forEach((_, variationValueIndex) => {
        const control = this.variationsFormService.getLinkedItemNameVariationValues(index, variationValueIndex);
        if (control) {
          if (this.splitGroupComponents) {
            control.setValidators([Validators.required]);
          } else {
            control.clearValidators();
          }
          control.updateValueAndValidity();
        }
      });
    });
  }

  ngOnInit(): void {
    if(this.splitGroupComponents) this.updateAllLinkedItemValidators();
    this.variationsFormService.variationValuesOrderChanged.update(v => v + 1);
  }

  listenToVariationsChanges() {
    this.variationsFormService.variationsArray?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(50),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe(() => {
        this.updateVariationNameSubscriptions();
        this.updateVariationValueSubscriptions();
      });
  }

  updateVariationNameSubscriptions(): void {
    const currentControls = new Set(this.variationsFormService.variationsArray?.value.map(v => v.tmp_id) || []);

    // Rimozione delle sottoscrizioni per i controlli che non esistono più
    this.variationNameSubscriptions.forEach((sub, control) => {
      if (!currentControls.has(control)) {
        sub.unsubscribe();
        this.variationNameSubscriptions.delete(control);
        this.variationsFormService.variationNameChanged.set({ index: 0 });
      }
    });

    // Aggiunta sottoscrizioni per i nuovi controlli o quelli esistenti non ancora sottoscritti
    this.variationsFormService.variationsArray?.controls.forEach((formGroup: any, variationIndex: number) => {
      if (!this.variationNameSubscriptions.has(formGroup.get('tmp_id')?.value)) {
        const nameControl = formGroup.get('name');
        if (nameControl) {
          const sub = nameControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((value: string) => {
              this.variationsFormService.variationNameChanged.set({ index: variationIndex });
            });
          this.variationNameSubscriptions.set(formGroup.get('tmp_id')?.value, sub);
          this.variationsFormService.variationNameChanged.set({ index: variationIndex });
        }
      }
    });
  }

  updateVariationValueSubscriptions() {
    const currentControls = new Set(this.variationsFormService.variationsArray?.value
        .flatMap(variation => {
          const innerList = variation.variation_values || [];
          return innerList.map(v => v.tmp_id);
        }) || []
    );

    // Rimozione delle sottoscrizioni per i controlli che non esistono più
    this.variationValueNameSubscriptions.forEach((sub, control) => {
      if (!currentControls.has(control)) {
        sub.unsubscribe();
        this.variationValueNameSubscriptions.delete(control);
        this.variationsFormService.variationValueNameChanged.set({ index: 0 });
      }
    });

    // Aggiunta sottoscrizioni per i nuovi controlli o quelli esistenti non ancora sottoscritti
    this.variationsFormService.variationsArray?.controls.forEach((formGroupVariation: any, variationIndex: number) => {
      this.variationsFormService.getVariationValues(variationIndex)?.controls.forEach((formGroupVariationValue: any, variationValueIndex: number) => {
        if (!this.variationValueNameSubscriptions.has(formGroupVariationValue.get('tmp_id')?.value)) {
          const nameControl = formGroupVariationValue.get('value');
          if (nameControl) {
            const sub = nameControl.valueChanges
              .pipe(takeUntil(this.destroy$))
              .subscribe((value: string) => {
                this.variationsFormService.variationValueNameChanged.set({ index: variationIndex });
              });
              this.variationValueNameSubscriptions.set(formGroupVariationValue.get('tmp_id')?.value, sub);
              this.variationsFormService.variationValueNameChanged.set({ index: variationIndex });
          }
        }
      })
    });
  }

  ngAfterViewInit(): void { 
    setTimeout(() => {
      if(this.accordion) {
        this.accordion.closeAll();
      }
    })
  }

  addNewVariation(variationName?: string) {
    this.variationsFormService.addNewVariation(variationName);

    if(!variationName) {
      this.panels.forEach(panel => panel.expanded = false);
      const panelId = this.variationsFormService.variationsArray?.length;
      this.scrollToPanel(panelId? panelId - 1: 0);
    }

    const lastIndex = this.variationsFormService.variationsArray?.value.length;
    this.variationsFormService.variationAdded.set([this.variationsFormService.variationsArray?.at(lastIndex? lastIndex - 1: 0)?.value]);
  }

  addVariationValue(index: number, variationValueName?: string) {
    this.variationsFormService.addVariationValue(index, variationValueName);

    if(!variationValueName) {
      const innerPanelId = this.variationsFormService.getVariationValues(index)?.length;
      this.scrollToPanel(index, innerPanelId? innerPanelId - 1: 0);
    }
  }

  copyVariation() {
    this.listSelectorDialogService.openDialog({
      data: {
        title: "DIALOG.LIST_SELECTOR.TITLE.ITEM",
        optionList: this.itemsWithVariation.filter(item => item.id != this.itemId), 
        optionValue: undefined
      }
    }
    ).then((resItem: Items | undefined) => {
      if(resItem?.variations?.length && resItem.variations.length > 0) {
        this.listSelectorDialogService.openDialog({
          data: {
            title: "DIALOG.LIST_SELECTOR.TITLE.VARIATION",
            optionList: resItem.variations, 
            optionValue: undefined,
            multiSelection: true
          }
        }
      ).then((resVariation: Array<ItemsVariations> | undefined) => {
        if(resVariation?.length) {
          this.variationsFormService.copyVariation(resVariation);
          this.variationsFormService.variationAdded.set(resVariation);
        }
      })
      }
    })
  }

  copyVariationFromCategory() {
    this.listSelectorDialogService.openDialog({
      data: {
        title: "DIALOG.LIST_SELECTOR.TITLE.CATEGORY",
        optionList: this.variationsFormService.categoriesWithVariation, 
        optionValue: undefined
      }
    }
    ).then((resCategory: Items | undefined) => {
      if(resCategory?.variations?.length && resCategory.variations.length > 0) {
        this.listSelectorDialogService.openDialog({
          data: {
            title: "DIALOG.LIST_SELECTOR.TITLE.CATEGORY",
            optionList: resCategory.variations, 
            optionValue: undefined,
            multiSelection: true
          }
        }
      ).then((resVariation: Array<Categories> | undefined) => {
        if(resVariation?.length) {
          this.variationsFormService.copyVariation(resVariation);
          this.variationsFormService.variationAdded.set(resVariation);
        }
      })
      }
    })
  }

  async removeVariation(variationIndex: number) {
    if(this.combinationsActive) {
      this.requestRemoveVariation.emit(variationIndex);
    } else {
      this.variationsFormService.removeVariation(variationIndex);
    }
  }

  manageAlternativeTranslateVariationName(variationIndex: number) {
    const config: TranslationsInputData = {
      mainValue: this.variationsFormService.getNameVariation(variationIndex)?.value,
      currentValues: this.variationsFormService.getAlternativeNamesVariation(variationIndex)?.value || [],
      maxLength: this.variationsFormService.validators.nameMaxLength
    }
    this.itemDataOperationsService.getUpdatedTranslations(config).then((updatedValues: AlternativeTranslate[] | null) => {
      if(updatedValues) this.variationsFormService.getAlternativeNamesVariation(variationIndex)?.setValue(updatedValues);
    })
  }

  removeVariationValue(variationIndex: number, variationValueIndex: number) {
    if(this.combinationsActive) {
      this.requestRemoveVariationValue.emit({variationIndex: variationIndex, variationValueIndex: variationValueIndex})
    } else {
      this.variationsFormService.removeVariationValue(variationIndex, variationValueIndex);
    }
  }

  onVariationRequiredChange(variationIndex: number) {
    const requiredControl = this.variationsFormService.getRequiredVariation(variationIndex);
    const minQtyControl = this.variationsFormService.getMinQuantityVariation(variationIndex);
    const maxQtyControl = this.variationsFormService.getMaxQuantityVariation(variationIndex);

    if (!requiredControl?.value && minQtyControl?.value > 0) {
      minQtyControl?.setValue(null);
    }

    minQtyControl?.updateValueAndValidity();
    maxQtyControl?.updateValueAndValidity();
  }

  onVariationQuantityMinChange(variationIndex: number) {
    const minControl = this.variationsFormService.getMinQuantityVariation(variationIndex);
    const maxControl = this.variationsFormService.getMaxQuantityVariation(variationIndex);
    const requiredControl = this.variationsFormService.getRequiredVariation(variationIndex);

    const minVal = minControl?.value || 0;
    requiredControl?.setValue(minVal > 0);

    minControl?.updateValueAndValidity();
    maxControl?.updateValueAndValidity();
  }

  onPriceDifferenceChange(variationIndex: number, variationValueIndex: number) {
    const priceControl = this.variationsFormService.getPriceDifferenceVariationValues(variationIndex, variationValueIndex);
    if(!this.variationsFormService.getPriceDifferenceTypeVariationValues(variationIndex, variationValueIndex)?.value && priceControl?.value)
      this.variationsFormService.getPriceDifferenceTypeVariationValues(variationIndex, variationValueIndex)?.setValue('value');
  }

  onVariationValueDefaultChange(variationIndex: number, valueIndex: number) {
    const variationValuesArray = this.variationsFormService.getVariationValues(variationIndex);
    const isCurrentDefault = this.variationsFormService.getDefaultValueVariationValues(variationIndex, valueIndex)?.value;

    if (isCurrentDefault) {
      variationValuesArray?.controls.forEach((control, index) => {
        if (index !== valueIndex) {
          this.variationsFormService.getDefaultValueVariationValues(variationIndex, index)?.setValue(false, { emitEvent: false });
        }
      });
    }
  }

  manageAlternativeTranslateVariationValueName(variationIndex: number, variationValueIndex: number) {
    const config: TranslationsInputData = {
      mainValue: this.variationsFormService.getNameVariationValues(variationIndex, variationValueIndex)?.value,
      currentValues: this.variationsFormService.getAlternativeNamesVariationValues(variationIndex, variationValueIndex)?.value || [],
      maxLength: this.variationsFormService.validators.nameVariantValueMaxLength
    }
    this.itemDataOperationsService.getUpdatedTranslations(config).then((updatedValues: AlternativeTranslate[] | null) => {
      if(updatedValues) this.variationsFormService.getAlternativeNamesVariationValues(variationIndex, variationValueIndex)?.setValue(updatedValues);
    })
  }

  openVariationPanel(variationIndex: number) {
    this.closeAllVariationValuePanels();
    this.variationsFormService.getExpandedVariation(variationIndex)?.setValue(true);
    if(this.combinationsActive) {
      this.closeAllCombinationsPanels.emit();
    }
  }

  closeVariationPanel(variationIndex: number) {
    this.variationsFormService.getExpandedVariation(variationIndex)?.setValue(false);
  }

  openVariationValuePanel(variationIndex: number, variationValueIndex: number) {
    this.variationsFormService.getExpandedVariationValues(variationIndex, variationValueIndex)?.setValue(true);
  }

  closeVariationValuePanel(variationIndex: number, variationValueIndex: number) {
    this.variationsFormService.getExpandedVariationValues(variationIndex, variationValueIndex)?.setValue(false);
  }

  closeAllVariantPanels(): void {
    if (this.accordion) {
      this.accordion.closeAll();
    }
  }

  closeAllVariationValuePanels(): void {
    if (this.innerAccordion) {
      this.innerAccordion.forEach(accordion => accordion.closeAll());
    }
  }

  onAfterPanelExpand(variationIndex: number): void {
    this.scrollToPanel(variationIndex);
  }

  onAfterPanelValueExpand(variationIndex: number, variationValueIndex: number): void {
    this.scrollToPanel(variationIndex, variationValueIndex);
  }

  /* Gestisce lo scroll al pannello della variante specificato dagli indici passati in input. 
  Il parametro panelId si riferisce al pannello esterno, mentre innerPanelId a quello interno. */
  scrollToPanel(panelId: number, innerPanelId?: number) {
    // Mi assicuro che il DOM sia aggiornato
    requestAnimationFrame(() => {
      let targetElement: HTMLElement | null = null;
      let elementToFocus: HTMLInputElement | null = null;

      if(innerPanelId != undefined) {
        this.elementRefPanels.forEach((el: ElementRef, i: number) => {
          const innerPanelContent = el.nativeElement.querySelector(`#inner-panel-content-${panelId}-${innerPanelId}`);
          // const panelContent = el.nativeElement.querySelector(`#panel-content-${panelId}`);
          if(innerPanelContent && !this.isMobile) {
            targetElement =innerPanelContent;

            if(targetElement) {
              elementToFocus = targetElement?.querySelector('input[formControlName="value"]') as HTMLInputElement; 
            }
          }
        })
      } else {
        // Cerco il pannello esterno
        targetElement = this.elementRefPanels?.find(
          (el: ElementRef) => el.nativeElement.querySelector(`#panel-content-${panelId}`)
        )?.nativeElement;
        
        if(targetElement && !this.isMobile) {
          elementToFocus = targetElement?.querySelector('input[formControlName="name"]') as HTMLInputElement; 
        }
      }
      
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
      
      if(elementToFocus) {
        elementToFocus.focus();
      }
    })

  }

  editVariationLinkedItem(variationValue: any, variationIndex: number, variationValueIndex: number) {
    let linkedItem;
    if(this.variationsFormService.getLinkedItemUUIDVariationValues(variationIndex, variationValueIndex)?.value) {
      linkedItem = this.variationLinkableItems.find(item => item.uuid == this.variationsFormService.getLinkedItemUUIDVariationValues(variationIndex, variationValueIndex)?.value)
    }
    this.listSelectorDialogService.openDialog({
        data: {
          title: "DIALOG.LIST_SELECTOR.TITLE.ITEM",
          optionList: this.variationLinkableItems, 
          optionValue: linkedItem
        }
      }
    ).then((res: any) => {
      if(res) {
        this.variationsFormService.getLinkedItemUUIDVariationValues(variationIndex, variationValueIndex)?.setValue(res.uuid)
        this.variationsFormService.getLinkedItemNameVariationValues(variationIndex, variationValueIndex)?.setValue(res.name);
        if(!this.variationsFormService.getNameVariationValues(variationIndex, variationValueIndex)?.value) {
          this.variationsFormService.getNameVariationValues(variationIndex, variationValueIndex)?.setValue(res.name);
        }
      }
    })
  }

  removeVariationLinkedItem(variationIndex: number, variationValueIndex: number) {
    this.variationsFormService.getLinkedItemUUIDVariationValues(variationIndex, variationValueIndex)?.setValue(null);
    this.variationsFormService.getLinkedItemNameVariationValues(variationIndex, variationValueIndex)?.setValue(null);
  }

  variantDropped($event: any) {
    if($event.previousIndex === $event.currentIndex) {
      return;
    }

    transferArrayItem(
      $event.previousContainer.data,
      $event.container.data,
      $event.previousIndex,
      $event.currentIndex
    );
    
    this.variationsFormService.variationsArray?.controls.forEach((variation: any, index: number) => {
      variation.get('index')?.setValue(index + 1);
    })

    this.variationsFormService.variationsOrderChanged.set(this.variationsFormService.variationsArray?.value);
  }

  variationValueDropped($event: any, variationIndex: number) {
    if($event.previousIndex === $event.currentIndex) {
      return;
    } 
    transferArrayItem(
      $event.previousContainer.data,
      $event.container.data,
      $event.previousIndex,
      $event.currentIndex
    );
    this.variationsFormService.getVariationValues(variationIndex)?.controls.forEach((variation, index) => {
      variation.get('index')?.setValue(index + 1);
    });

    this.variationsFormService.variationValuesOrderChanged.update(v => v + 1);
  }

  getVariationDescription(variationIndex: number) {
    let description = '';
    if(this.variationsFormService.getMinQuantityVariation(variationIndex)?.value) {
      description += 'min. ' + this.variationsFormService.getMinQuantityVariation(variationIndex)?.value;
    }
    if(this.variationsFormService.getMaxQuantityVariation(variationIndex)?.value) {
      description += ' max. ' + this.variationsFormService.getMaxQuantityVariation(variationIndex)?.value;
    }
    return description;
  }

  getVariationValueName(variationIndex: number) {
    const controls = this.variationsFormService.getVariationValues(variationIndex)?.controls || [];

    const names = controls
      .map((_, i) => this.variationsFormService.getNameVariationValues(variationIndex, i)?.value)
      .filter(name => !!name); 

    if (names.length === 0) return '';

    return `(${names.join(', ')})`;
  }

  getVariationValueDescription(variationIndex: number, variationValueIndex: number) {
    let description = '';
    if(this.variationsFormService.getPriceDifferenceVariationValues(variationIndex, variationValueIndex)?.value) {
      description += this.variationsFormService.getPriceDifferenceVariationValues(variationIndex, variationValueIndex)?.value;
    }
    if(this.variationsFormService.getPriceDifferenceTypeVariationValues(variationIndex, variationValueIndex)?.value) {
      if(this.variationsFormService.getPriceDifferenceTypeVariationValues(variationIndex, variationValueIndex)?.value == 'value') {
        description += ' ' + TilbyCurrencyPipe.currency.symbol_native;
      } else {
        description += ' %';
      }
    }
    return description;
  }

  // INSERIMENTO IMMAGINE 

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  maxImageWidth: number = 1280;
  maxImageHeight: number = 720;

  addNewImage() {
    this.fileInput.nativeElement.click();
  }

  handleFileInput(event: Event, variantIndex: number, variantValueIndex: number) {
    // console.log("handleFileInput ", event);
    const input = event.target as HTMLInputElement;

    if(input.files?.length) {
      const newFile = input.files[0];
      this.processNewFile(newFile, variantIndex, variantValueIndex);
    }
  }

  private processNewFile(file: File, variantIndex: number, variantValueIndex: number) {
    ImagesManagerService.localLoadNewImage(file, this.maxImageWidth, this.maxImageHeight).then((result: any) => {
      this.variationsFormService.getImageVariationValues(variantIndex, variantValueIndex)?.setValue(result);
      this.variationsFormService.getImageUrlVariationValues(variantIndex, variantValueIndex)?.setValue(result?.image_url);
    })
    .catch((reason) => {
      // invalid file
      switch (reason) {
        case 'not image':
          this.alertDialogService.openDialog({
            data: {
              messageLabel: this.translate.instant('ITEMS.DETAILS.COMPONENT_INVALID_IMAGE')
          }});
          break;
        default:
          this.alertDialogService.openDialog({
            data: {
              messageLabel: this.translate.instant('ITEMS.DETAILS.COMPONENT_PROBLEM_OPENING_IMAGE')
          }});
      }
    });
  }

  removeImage(variantIndex: number, variantValueIndex: number) {
    if(this.variationsFormService.getImageUrlVariationValues(variantIndex, variantValueIndex)?.value) {
      ImagesManagerService.markToDelete(this.variationsFormService.getImageVariationValues(variantIndex, variantValueIndex)?.value);
      this.variationsFormService.getImageUrlVariationValues(variantIndex, variantValueIndex)?.setValue(null);
    } else {
      this.variationsFormService.getImageVariationValues(variantIndex, variantValueIndex)?.setValue(null);
    }
  }

  // ---- INSERIMENTO IMMAGINE 

  ngOnDestroy() {
    if (this.focusSubscription) {
      this.focusSubscription.unsubscribe();
    }

    this.variationsFormReady.set(false);

    this.destroy$.next();
    this.destroy$.complete();

    this.variationNameSubscriptions.forEach(sub => sub.unsubscribe());
    this.variationNameSubscriptions.clear();

    this.variationValueNameSubscriptions.forEach(sub => sub.unsubscribe());
    this.variationValueNameSubscriptions.clear();
  }
}
